import { useState } from 'react';

import { CourseAdminCard } from '@/components/shared/CourseAdminCard';
import { Badge } from '@/components/ui/daisy';
import { ListCacheManager } from '@/data/hook/query/v2/CacheFacade';
import { Assessment } from '@/models/Assessment';
import CoursePath from '@/models/CoursePath';
import Evaluation from '@/models/Evaluation';
import AssessmentsList from './AssessmentsList';
import EvaluationBlockInlineForm, {
  EvaluationBlockInlineFormFields,
} from './EvaluationBlockInlineForm';

type EvaluationBlockCardProps = {
  evaluation: Evaluation;
  cacheManager: ListCacheManager<Evaluation>;
  appliedCoursePaths?: CoursePath[];
  courseLineId?: number;
  onDelete?: (evaluationId: number) => Promise<void>;
  onUpdate?: (
    evaluation: Evaluation,
    data: EvaluationBlockInlineFormFields,
  ) => Promise<void>;
  onAddAssessment: () => void;
  onEditAssessment: (assessment: Assessment) => void;
  isUpdating?: boolean;
};

export default function EvaluationBlockCard({
  evaluation,
  cacheManager,
  appliedCoursePaths,
  courseLineId,
  onDelete,
  onUpdate,
  onAddAssessment,
  onEditAssessment,
  isUpdating,
}: EvaluationBlockCardProps) {
  const [isEditing, setIsEditing] = useState(false);

  const handleUpdate = async (data: EvaluationBlockInlineFormFields) => {
    if (!onUpdate) return;
    await onUpdate(evaluation, {
      name: data.name,
      coursePaths: data.coursePaths,
    });
    setIsEditing(false);
  };

  if (isEditing && onUpdate) {
    return (
      <EvaluationBlockInlineForm
        defaultValues={{
          name: evaluation.name,
          coursePaths: appliedCoursePaths ?? [],
        }}
        onSubmit={handleUpdate}
        courseLineId={courseLineId}
        onCancel={() => setIsEditing(false)}
        isLoading={isUpdating}
      />
    );
  }

  return (
    <CourseAdminCard variant="block">
      <CourseAdminCard.Header>
        <CourseAdminCard.Leading className="flex-wrap">
          <CourseAdminCard.Title>{evaluation.name}</CourseAdminCard.Title>
          {appliedCoursePaths?.map(coursePath => (
            <Badge
              key={coursePath.id}
              variant="soft"
              size="md"
              data-testid="course-path-badge"
            >
              {coursePath.course.abbreviation}
            </Badge>
          ))}
        </CourseAdminCard.Leading>
        <CourseAdminCard.Actions>
          {onUpdate && (
            <CourseAdminCard.EditButton onClick={() => setIsEditing(true)} />
          )}
          {onDelete && (
            <CourseAdminCard.MoreActions>
              <CourseAdminCard.MoreActions.Delete
                onClick={() => onDelete(evaluation.id)}
                confirm
              />
            </CourseAdminCard.MoreActions>
          )}
        </CourseAdminCard.Actions>
      </CourseAdminCard.Header>
      <AssessmentsList
        evaluation={evaluation}
        cacheManager={cacheManager}
        onAddAssessment={onAddAssessment}
        onEditAssessment={onEditAssessment}
      />
    </CourseAdminCard>
  );
}
